import { QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from 'next-themes'
import { lazy, type ReactNode, Suspense } from 'react'
import { Provider } from 'react-redux'
import type { Location } from 'react-router-dom'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { AppLayout } from '@/components/layout/AppLayout'
import { MaintenanceGate } from '@/components/routes/MaintenanceGate'
import { ProtectedRoute } from '@/components/routes/ProtectedRoute'
import { PublicRoute } from '@/components/routes/PublicRoute'
import { SuperAdminRoute } from '@/components/routes/SuperAdminRoute'
import { AppLockGate } from '@/components/shared/AppLockGate'
import { DetailSheet } from '@/components/shared/DetailSheet'
import { ErrorBoundary } from '@/components/shared/ErrorBoundary'
import { GlobalLoadingOverlay } from '@/components/shared/GlobalLoadingOverlay'
import { FullPageLoader } from '@/components/shared/LoadingSpinner'
import { NativeBridge } from '@/components/shared/NativeBridge'
import { PdfPreviewOverlay } from '@/components/shared/PdfPreviewOverlay'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { Toaster } from '@/components/ui/toaster'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AbilityProvider } from '@/contexts/AbilityContext'
import { queryClient } from '@/hooks/useAuth'
import { store } from '@/store'

import { APP_ROUTES, ROUTE_PERMISSIONS } from './libs/constants'

// Auth pages
const ForgotPassword = lazy(() => import('./pages/main/auth/ForgotPassword'))
const Login = lazy(() => import('./pages/main/auth/Login'))
const AdminLogin = lazy(() => import('./pages/admin/auth/AdminLogin'))
const AdminForgotPassword = lazy(() => import('./pages/admin/auth/AdminForgotPassword'))
const ResetPassword = lazy(() => import('./pages/main/auth/ResetPassword'))
const AcceptInvite = lazy(() => import('./pages/main/auth/AcceptInvite'))
const AdminResetPassword = lazy(() => import('./pages/admin/auth/AdminResetPassword'))

// Standalone pages (no layout)
const AccessDenied = lazy(() => import('./pages/AccessDenied'))
const Maintenance = lazy(() => import('./pages/Maintenance'))
const SomethingWentWrong = lazy(() => import('./pages/SomethingWentWrong'))
const NotFound = lazy(() => import('./pages/NotFound'))
const SelectOrganization = lazy(() => import('./pages/main/SelectOrganization'))

// Main pages
const Account = lazy(() => import('./pages/main/auth/Account'))
const Billing = lazy(() => import('./pages/main/invoicing'))
const InvoiceDetails = lazy(() => import('./pages/main/invoicing/InvoiceDetails'))
const NewInvoice = lazy(() => import('./pages/main/invoicing/NewInvoice'))
const Booking = lazy(() => import('./pages/main/booking'))
const NewBooking = lazy(() => import('./pages/main/booking/NewBooking'))
const Campaigns = lazy(() => import('./pages/main/Campaigns'))
const CategoriesPage = lazy(() => import('./pages/main/categories'))
const CategoryDetails = lazy(() => import('./pages/main/categories/CategoryDetails'))
const NewCategory = lazy(() => import('./pages/main/categories/NewCategory'))
const ChangePassword = lazy(() => import('./pages/main/auth/ChangePassword'))
const CommunicationPage = lazy(() => import('./pages/main/communication'))
const TemplateDetails = lazy(() => import('./pages/main/communication/TemplateDetails'))
const TemplateEditor = lazy(() => import('./pages/main/communication/TemplateEditor'))
const CustomersPage = lazy(() => import('./pages/main/customers'))
const CustomerDetails = lazy(() => import('./pages/main/customers/CustomerDetails'))
const NewCompany = lazy(() => import('./pages/main/customers/NewCompany'))
const NewCustomer = lazy(() => import('./pages/main/customers/NewCustomer'))
const CompanyDetails = lazy(() => import('./pages/main/customers/CompanyDetails'))
const Economy = lazy(() => import('./pages/main/Economy'))
const Index = lazy(() => import('./pages/Index'))
const Jobs = lazy(() => import('./pages/main/jobs'))
const JobDetails = lazy(() => import('./pages/main/jobs/JobDetails'))
const MyWorkspace = lazy(() => import('./pages/main/my-workspace'))
const Pricing = lazy(() => import('./pages/main/Pricing'))
const Products = lazy(() => import('./pages/main/products'))
const NewProduct = lazy(() => import('./pages/main/products/NewProduct'))
const ProductDetails = lazy(() => import('./pages/main/products/ProductDetails'))
const RentalFleet = lazy(() => import('./pages/main/rental-fleet'))
const NewRentalVehicle = lazy(() => import('./pages/main/rental-fleet/NewRentalVehicle'))
const RentalVehicleDetails = lazy(() => import('./pages/main/rental-fleet/RentalVehicleDetails'))
const Services = lazy(() => import('./pages/main/settings/services'))
const NewService = lazy(() => import('./pages/main/settings/services/NewService'))
const ServiceDetails = lazy(() => import('./pages/main/settings/services/ServiceDetails'))
const BranchInfo = lazy(() => import('./pages/main/settings/BranchInfo'))
const NewStaff = lazy(() => import('./pages/main/settings/staff/NewStaff'))
const AbsenceTypes = lazy(() => import('./pages/main/settings/absenceTypes'))
const NewAbsenceType = lazy(() => import('./pages/main/settings/absenceTypes/NewAbsenceType'))
const AbsenceTypeDetails = lazy(
  () => import('./pages/main/settings/absenceTypes/AbsenceTypeDetails'),
)
const Packages = lazy(() => import('./pages/main/settings/packages'))
const NewPackage = lazy(() => import('./pages/main/settings/packages/NewPackage'))
const PackageDetails = lazy(() => import('./pages/main/settings/packages/PackageDetails'))
const Permissions = lazy(() => import('./pages/main/settings/Permissions'))
const Qualifications = lazy(() => import('./pages/main/settings/qualifications'))
const NewQualification = lazy(() => import('./pages/main/settings/qualifications/NewQualification'))
const QualificationDetails = lazy(
  () => import('./pages/main/settings/qualifications/QualificationDetails'),
)
const SettingsLayout = lazy(() => import('./pages/main/settings/SettingsLayout'))
const Staff = lazy(() => import('./pages/main/settings/staff'))
const StaffDetails = lazy(() => import('./pages/main/settings/staff/StaffDetails'))
const NewStore = lazy(() => import('./pages/main/settings/stores/NewStore'))
const StoreDetails = lazy(() => import('./pages/main/settings/stores/StoreDetails'))
const Stores = lazy(() => import('./pages/main/settings/stores'))
const UnionAgreements = lazy(() => import('./pages/main/settings/union-agreements'))
const NewUnionAgreement = lazy(
  () => import('./pages/main/settings/union-agreements/NewUnionAgreement'),
)
const UnionAgreementDetails = lazy(
  () => import('./pages/main/settings/union-agreements/UnionAgreementDetails'),
)
const WeeklyNotesSettings = lazy(() => import('./pages/main/settings/WeeklyNotes'))
const WorkingHours = lazy(() => import('./pages/main/settings/WorkingHours'))
const TeamOverviewHub = lazy(() => import('./pages/main/employee-approvals/TeamOverviewHub'))
const Vehicles = lazy(() => import('./pages/main/vehicles'))
const NewVehicle = lazy(() => import('./pages/main/vehicles/NewVehicle'))
const VehicleDetails = lazy(() => import('./pages/main/vehicles/VehicleDetails'))

// Admin panel
const AdminLayout = lazy(() =>
  import('./components/admin/AdminLayout').then((m) => ({
    default: m.AdminLayout,
  })),
)
const AdminOverview = lazy(() => import('./pages/admin'))
const AdminOrganizations = lazy(() => import('./pages/admin/organization/Organizations'))
const AdminOrganizationDetails = lazy(
  () => import('./pages/admin/organization/OrganizationDetails'),
)
const AdminNewOrganization = lazy(() => import('./pages/admin/organization/NewOrganization'))
const AdminSubscriptions = lazy(() => import('./pages/admin/subscriptions/Subscriptions'))
const AdminNewSubscription = lazy(() => import('./pages/admin/subscriptions/NewSubscription'))
const AdminSubscriptionDetails = lazy(
  () => import('./pages/admin/subscriptions/SubscriptionDetails'),
)
const AdminNewPlan = lazy(() => import('./pages/admin/plans/NewPlan'))
const AdminPlanDetails = lazy(() => import('./pages/admin/plans/PlanDetails'))
const AdminDiscounts = lazy(() => import('./pages/admin/discount/Discounts'))
const AdminNewDiscount = lazy(() => import('./pages/admin/discount/NewDiscount'))
const AdminDiscountDetails = lazy(() => import('./pages/admin/discount/DiscountDetails'))
const AdminGlobalRoles = lazy(
  () => import('./pages/admin/role-and-permissions/RolesAndPermissions'),
)
const AdminNewGlobalRole = lazy(() => import('./pages/admin/role-and-permissions/NewGlobalRole'))
const AdminGlobalRoleDetails = lazy(
  () => import('./pages/admin/role-and-permissions/GlobalRoleDetails'),
)
const AdminFeatures = lazy(() => import('./pages/admin/features/Features'))
const AdminNewFeature = lazy(() => import('./pages/admin/features/NewFeature'))
const AdminFeatureDetails = lazy(() => import('./pages/admin/features/FeatureDetails'))
const AdminUsage = lazy(() => import('./pages/admin/usage/Usage'))
const AdminUsageDetails = lazy(() => import('./pages/admin/usage/UsageDetails'))
const AdminUsers = lazy(() => import('./pages/admin/Users'))
const AdminUserDetails = lazy(() => import('./pages/admin/usage/UserDetails'))
const AdminSalesCompanies = lazy(() => import('./pages/admin/sales-companies/SalesCompanies'))
const AdminNewSalesCompany = lazy(() => import('./pages/admin/sales-companies/NewSalesCompany'))
const AdminSalesCompanyDetails = lazy(
  () => import('./pages/admin/sales-companies/SalesCompanyDetails'),
)

queryClient.setDefaultOptions({
  queries: {
    retry: 1,
    refetchOnWindowFocus: false,
  },
})

// Every listing page's row click opens its details page as a right-side
// quick-view sheet instead of navigating away, via React Router's
// "background location" pattern: the row click pushes the details URL with
// `state: { backgroundLocation: location }`, the primary <Routes> tree in
// AppRoutes keeps matching the background (list) location so the list stays
// mounted, and this second, flat route tree matches the real (details)
// location on top of it, wrapped in <DetailSheet>. A direct visit to a
// details URL (a bookmark, a refresh, or a listing page's "view details"
// quick action, neither of which sets `backgroundLocation`) never reaches
// this tree at all — it falls through to the existing nested route in
// AppRoutes and renders as an ordinary full page, unchanged.
const detailRoutes: { path: string; element: ReactNode }[] = [
  {
    path: `${APP_ROUTES.customer.base}/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.customers.read}>
        <DetailSheet>
          <CustomerDetails />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.customer.company.base}/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.company.read}>
        <DetailSheet>
          <CompanyDetails />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.vehicle.base}/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.vehicles.read}>
        <DetailSheet>
          <VehicleDetails />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.job.base}/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.jobs.read}>
        <DetailSheet>
          <JobDetails />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.invoicing.base}/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.billing.read}>
        <DetailSheet>
          <InvoiceDetails />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.product.base}/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.products.read}>
        <DetailSheet>
          <ProductDetails />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.rentCar.base}/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.rentalFleet.read}>
        <DetailSheet>
          <RentalVehicleDetails />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.settings.staff.base}/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.staff.read}>
        <DetailSheet>
          <StaffDetails />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.settings.stores.base}/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.stores.read}>
        <DetailSheet>
          <StoreDetails />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.settings.qualifications.base}/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.qualifications.read}>
        <DetailSheet>
          <QualificationDetails />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.settings.unionAgreements.base}/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.unionAgreements.read}>
        <DetailSheet>
          <UnionAgreementDetails />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.settings.packages.base}/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.packages.read}>
        <DetailSheet>
          <PackageDetails />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.settings.absenceTypes.base}/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.absenceTypes.read}>
        <DetailSheet>
          <AbsenceTypeDetails />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.settings.category.base}/service/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.categories.read}>
        <DetailSheet>
          <CategoryDetails type="service" />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.settings.category.base}/product/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.categories.read}>
        <DetailSheet>
          <CategoryDetails type="product" />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.settings.service.base}/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.services.read}>
        <DetailSheet>
          <ServiceDetails />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.settings.communication.base}/email/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.communication.read}>
        <DetailSheet>
          <TemplateDetails type="email" />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.settings.communication.base}/sms/:id`,
    element: (
      <ProtectedRoute {...ROUTE_PERMISSIONS.communication.read}>
        <DetailSheet>
          <TemplateDetails type="sms" />
        </DetailSheet>
      </ProtectedRoute>
    ),
  },
  {
    path: `${APP_ROUTES.admin.base}/organizations/:id`,
    element: (
      <SuperAdminRoute>
        <DetailSheet>
          <AdminOrganizationDetails />
        </DetailSheet>
      </SuperAdminRoute>
    ),
  },
  {
    path: `${APP_ROUTES.admin.base}/subscriptions/:id`,
    element: (
      <SuperAdminRoute>
        <DetailSheet>
          <AdminSubscriptionDetails />
        </DetailSheet>
      </SuperAdminRoute>
    ),
  },
  {
    path: `${APP_ROUTES.admin.base}/plans/:id`,
    element: (
      <SuperAdminRoute>
        <DetailSheet>
          <AdminPlanDetails />
        </DetailSheet>
      </SuperAdminRoute>
    ),
  },
  {
    path: `${APP_ROUTES.admin.base}/features/:id`,
    element: (
      <SuperAdminRoute>
        <DetailSheet>
          <AdminFeatureDetails />
        </DetailSheet>
      </SuperAdminRoute>
    ),
  },
  {
    path: `${APP_ROUTES.admin.base}/usage/:id`,
    element: (
      <SuperAdminRoute>
        <DetailSheet>
          <AdminUsageDetails />
        </DetailSheet>
      </SuperAdminRoute>
    ),
  },
  {
    path: `${APP_ROUTES.admin.base}/discounts/:id`,
    element: (
      <SuperAdminRoute>
        <DetailSheet>
          <AdminDiscountDetails />
        </DetailSheet>
      </SuperAdminRoute>
    ),
  },
  {
    path: `${APP_ROUTES.admin.base}/roles/:id`,
    element: (
      <SuperAdminRoute>
        <DetailSheet>
          <AdminGlobalRoleDetails />
        </DetailSheet>
      </SuperAdminRoute>
    ),
  },
  {
    path: `${APP_ROUTES.admin.base}/users/:id`,
    element: (
      <SuperAdminRoute>
        <DetailSheet>
          <AdminUserDetails />
        </DetailSheet>
      </SuperAdminRoute>
    ),
  },
  {
    path: `${APP_ROUTES.admin.base}/sales-companies/:id`,
    element: (
      <SuperAdminRoute>
        <DetailSheet>
          <AdminSalesCompanyDetails />
        </DetailSheet>
      </SuperAdminRoute>
    ),
  },
]

function AppRoutes() {
  const location = useLocation()
  const backgroundLocation = (location.state as { backgroundLocation?: Location } | null)
    ?.backgroundLocation

  return (
    <>
      <Suspense fallback={<FullPageLoader />}>
        <Routes location={backgroundLocation ?? location}>
          {/* Auth pages - public only */}
          <Route
            path={APP_ROUTES.auth.login}
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path={APP_ROUTES.auth.adminLogin}
            element={
              <PublicRoute>
                <AdminLogin />
              </PublicRoute>
            }
          />
          <Route
            path={APP_ROUTES.auth.forgotPassword}
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path={APP_ROUTES.auth.resetPassword}
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />
          <Route
            path={APP_ROUTES.acceptInvite}
            element={
              <PublicRoute>
                <AcceptInvite />
              </PublicRoute>
            }
          />
          <Route
            path={APP_ROUTES.auth.adminForgotPassword}
            element={
              <PublicRoute>
                <AdminForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path={APP_ROUTES.auth.adminResetPassword}
            element={
              <PublicRoute>
                <AdminResetPassword />
              </PublicRoute>
            }
          />

          {/* Auth-required, no layout */}
          <Route
            path={APP_ROUTES.selectOrganization}
            element={
              <ProtectedRoute>
                <SelectOrganization />
              </ProtectedRoute>
            }
          />

          {/* Protected app pages - single auth check + layout wrapper */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route index element={<Index />} />

              {/* Customers */}
              <Route
                path={APP_ROUTES.customer.base}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.customers.read}>
                    <CustomersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={APP_ROUTES.customer.new}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.customers.create}>
                    <NewCustomer />
                  </ProtectedRoute>
                }
              />
              <Route
                path={APP_ROUTES.customer.company.create}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.company.create}>
                    <NewCompany />
                  </ProtectedRoute>
                }
              />
              <Route
                path={`${APP_ROUTES.customer.company.base}/:id`}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.company.read}>
                    <CompanyDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path={`${APP_ROUTES.customer.base}/:id`}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.customers.read}>
                    <CustomerDetails />
                  </ProtectedRoute>
                }
              />

              {/* Vehicles */}
              <Route
                path={APP_ROUTES.vehicle.base}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.vehicles.read}>
                    <Vehicles />
                  </ProtectedRoute>
                }
              />
              <Route
                path={APP_ROUTES.vehicle.new}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.vehicles.create}>
                    <NewVehicle />
                  </ProtectedRoute>
                }
              />
              <Route
                path={`${APP_ROUTES.vehicle.base}/:id`}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.vehicles.read}>
                    <VehicleDetails />
                  </ProtectedRoute>
                }
              />

              {/* Jobs */}
              <Route
                path={APP_ROUTES.job.base}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.jobs.read}>
                    <Jobs />
                  </ProtectedRoute>
                }
              />
              <Route
                path={`${APP_ROUTES.job.base}/:id`}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.jobs.read}>
                    <JobDetails />
                  </ProtectedRoute>
                }
              />

              {/* Booking */}
              <Route
                path={APP_ROUTES.booking.base}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.booking.read}>
                    <Booking />
                  </ProtectedRoute>
                }
              />
              <Route
                path={APP_ROUTES.booking.new()}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.booking.create}>
                    <NewBooking />
                  </ProtectedRoute>
                }
              />

              {/* Pricing */}
              <Route
                path={APP_ROUTES.pricing}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.pricing.read}>
                    <Pricing />
                  </ProtectedRoute>
                }
              />

              {/* Workspace */}
              <Route
                path={APP_ROUTES.myWorkspace}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.myWorkspace.read}>
                    <MyWorkspace />
                  </ProtectedRoute>
                }
              />
              <Route
                path={APP_ROUTES.teamOverview}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.teamOverview.read}>
                    <TeamOverviewHub />
                  </ProtectedRoute>
                }
              />
              {/* Legacy work redirects */}
              <Route
                path={APP_ROUTES.leaves}
                element={<Navigate to={`${APP_ROUTES.myWorkspace}?tab=leaves`} replace />}
              />
              <Route
                path={APP_ROUTES.workLogs}
                element={<Navigate to={APP_ROUTES.myWorkspace} replace />}
              />
              <Route
                path={`${APP_ROUTES.workLogs}/my`}
                element={<Navigate to={APP_ROUTES.myWorkspace} replace />}
              />
              <Route
                path={`${APP_ROUTES.workLogs}/team`}
                element={<Navigate to={APP_ROUTES.teamOverview} replace />}
              />

              {/* Invoicing */}
              <Route
                path={APP_ROUTES.invoicing.base}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.billing.read}>
                    <Billing />
                  </ProtectedRoute>
                }
              />
              <Route
                path={APP_ROUTES.invoicing.new}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.billing.create}>
                    <NewInvoice />
                  </ProtectedRoute>
                }
              />
              <Route
                path={`${APP_ROUTES.invoicing.base}/:id`}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.billing.read}>
                    <InvoiceDetails />
                  </ProtectedRoute>
                }
              />
              {/* Legacy billing redirects */}
              <Route
                path={APP_ROUTES.billing}
                element={<Navigate to={APP_ROUTES.invoicing.base} replace />}
              />
              <Route
                path={`${APP_ROUTES.billing}/new`}
                element={<Navigate to={APP_ROUTES.invoicing.new} replace />}
              />
              <Route
                path={`${APP_ROUTES.billing}/:id`}
                element={<Navigate to={APP_ROUTES.invoicing.base} replace />}
              />

              {/* Economy */}
              <Route
                path={APP_ROUTES.economy}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.billing.read}>
                    <Economy />
                  </ProtectedRoute>
                }
              />

              {/* Products */}
              <Route
                path={APP_ROUTES.product.base}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.products.read}>
                    <Products />
                  </ProtectedRoute>
                }
              />
              <Route
                path={APP_ROUTES.product.new}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.products.create}>
                    <NewProduct />
                  </ProtectedRoute>
                }
              />
              <Route
                path={`${APP_ROUTES.product.base}/:id`}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.products.read}>
                    <ProductDetails />
                  </ProtectedRoute>
                }
              />

              {/* Rental Fleet */}
              <Route
                path={APP_ROUTES.rentCar.base}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.rentalFleet.read}>
                    <RentalFleet />
                  </ProtectedRoute>
                }
              />
              <Route
                path={APP_ROUTES.rentCar.new}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.rentalFleet.create}>
                    <NewRentalVehicle />
                  </ProtectedRoute>
                }
              />
              <Route
                path={`${APP_ROUTES.rentCar.base}/:id`}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.rentalFleet.read}>
                    <RentalVehicleDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path={APP_ROUTES.loanCars}
                element={<Navigate to={APP_ROUTES.rentCar.base} replace />}
              />

              {/* Campaigns */}
              <Route
                path={APP_ROUTES.campaigns}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.campaigns.read}>
                    <Campaigns />
                  </ProtectedRoute>
                }
              />

              {/* Legacy redirects for sections moved to settings */}
              <Route
                path={APP_ROUTES.category.base}
                element={<Navigate to={APP_ROUTES.settings.category.base} replace />}
              />
              <Route
                path={APP_ROUTES.communication.base}
                element={<Navigate to={APP_ROUTES.settings.communication.base} replace />}
              />

              {/* Settings */}
              <Route
                path={APP_ROUTES.settings.base}
                element={
                  <ProtectedRoute {...ROUTE_PERMISSIONS.settings.read}>
                    <SettingsLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to={APP_ROUTES.settings.branchInfo} replace />} />
                <Route path="branch-info" element={<BranchInfo />} />
                <Route path="working-hours" element={<WorkingHours />} />
                <Route
                  path="staff"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.staff.read}>
                      <Staff />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="staff/new"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.staff.create}>
                      <NewStaff />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="staff/:id"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.staff.read}>
                      <StaffDetails />
                    </ProtectedRoute>
                  }
                />
                <Route path="permissions" element={<Permissions />} />
                <Route
                  path="stores"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.stores.read}>
                      <Stores />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="stores/new"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.stores.create}>
                      <NewStore />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="stores/:id"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.stores.read}>
                      <StoreDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="qualifications"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.qualifications.read}>
                      <Qualifications />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="qualifications/new"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.qualifications.create}>
                      <NewQualification />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="qualifications/:id"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.qualifications.read}>
                      <QualificationDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="union-agreements"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.unionAgreements.read}>
                      <UnionAgreements />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="union-agreements/new"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.unionAgreements.create}>
                      <NewUnionAgreement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="union-agreements/:id"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.unionAgreements.read}>
                      <UnionAgreementDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="packages"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.packages.read}>
                      <Packages />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="packages/new"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.packages.create}>
                      <NewPackage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="packages/:id"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.packages.read}>
                      <PackageDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="absence-types"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.absenceTypes.read}>
                      <AbsenceTypes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="absence-types/new"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.absenceTypes.create}>
                      <NewAbsenceType />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="absence-types/:id"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.absenceTypes.read}>
                      <AbsenceTypeDetails />
                    </ProtectedRoute>
                  }
                />
                <Route path="weekly-notes" element={<WeeklyNotesSettings />} />
                {/* <Route path="integrations" element={<Integrations />} /> */}
                <Route
                  path="categories"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.categories.read}>
                      <CategoriesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="categories/service/new"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.categories.create}>
                      <NewCategory type="service" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="categories/product/new"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.categories.create}>
                      <NewCategory type="product" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="categories/service/:id"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.categories.read}>
                      <CategoryDetails type="service" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="categories/product/:id"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.categories.read}>
                      <CategoryDetails type="product" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="services"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.services.read}>
                      <Services />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="services/new"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.services.create}>
                      <NewService />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="services/:id"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.services.read}>
                      <ServiceDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="communication"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.communication.read}>
                      <CommunicationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="communication/email/new"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.communication.create}>
                      <TemplateEditor type="email" mode="create" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="communication/sms/new"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.communication.create}>
                      <TemplateEditor type="sms" mode="create" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="communication/email/:id"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.communication.read}>
                      <TemplateDetails type="email" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="communication/sms/:id"
                  element={
                    <ProtectedRoute {...ROUTE_PERMISSIONS.communication.read}>
                      <TemplateDetails type="sms" />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Account */}
              <Route path={APP_ROUTES.account} element={<Account />} />
              <Route path={APP_ROUTES.changePassword} element={<ChangePassword />} />
            </Route>
          </Route>

          {/* Fallback */}
          <Route path={APP_ROUTES.accessDenied} element={<AccessDenied />} />
          <Route path={APP_ROUTES.somethingWentWrong} element={<SomethingWentWrong />} />
          <Route path={APP_ROUTES.maintenance} element={<Maintenance />} />

          {/* Admin panel — superadmin only */}
          <Route element={<SuperAdminRoute />}>
            <Route path={APP_ROUTES.admin.base} element={<AdminLayout />}>
              <Route index element={<AdminOverview />} />
              <Route path="organizations" element={<AdminOrganizations />} />
              <Route path="organizations/new" element={<AdminNewOrganization />} />
              <Route path="organizations/:id" element={<AdminOrganizationDetails />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="subscriptions/new" element={<AdminNewSubscription />} />
              <Route path="subscriptions/:id" element={<AdminSubscriptionDetails />} />
              <Route
                path="plans"
                element={<Navigate to={APP_ROUTES.admin.subscriptions} replace />}
              />
              <Route path="plans/new" element={<AdminNewPlan />} />
              <Route path="plans/:id" element={<AdminPlanDetails />} />
              <Route path="features" element={<AdminFeatures />} />
              <Route path="features/new" element={<AdminNewFeature />} />
              <Route path="features/:id" element={<AdminFeatureDetails />} />
              <Route path="usage" element={<AdminUsage />} />
              <Route path="usage/:id" element={<AdminUsageDetails />} />
              <Route path="discounts" element={<AdminDiscounts />} />
              <Route path="discounts/new" element={<AdminNewDiscount />} />
              <Route path="discounts/:id" element={<AdminDiscountDetails />} />
              <Route path="roles" element={<AdminGlobalRoles />} />
              <Route path="roles/new" element={<AdminNewGlobalRole />} />
              <Route path="roles/:id" element={<AdminGlobalRoleDetails />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="users/:id" element={<AdminUserDetails />} />
              <Route path="sales-companies" element={<AdminSalesCompanies />} />
              <Route path="sales-companies/new" element={<AdminNewSalesCompany />} />
              <Route path="sales-companies/:id" element={<AdminSalesCompanyDetails />} />
              <Route path="account" element={<Account />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>

      {backgroundLocation && (
        <Suspense fallback={<FullPageLoader />}>
          <Routes location={location}>
            {detailRoutes.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
          </Routes>
        </Suspense>
      )}
    </>
  )
}

const App = () => (
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <AbilityProvider>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
          <TooltipProvider>
            <ErrorBoundary>
              <NativeBridge />
              <Toaster />
              <Sonner />
              <GlobalLoadingOverlay />
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <AppLockGate />
                <PdfPreviewOverlay />
                <MaintenanceGate>
                  <AppRoutes />
                </MaintenanceGate>
              </BrowserRouter>
            </ErrorBoundary>
          </TooltipProvider>
        </ThemeProvider>
      </AbilityProvider>
    </QueryClientProvider>
  </Provider>
)

export default App
